import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { errorMessage } from "../lib/errors";
import { AlertTriangle, BookOpen, ClipboardList, Clock, Award, ArrowRight, Lock, CheckCircle2 } from "lucide-react";

export default function CertificationDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [cert, setCert] = useState(null);
  const [access, setAccess] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/certifications/${slug}`).then(r => setCert(r.data)).catch(() => setNotFound(true));
    if (user) api.get(`/certifications/${slug}/access`).then(r => setAccess(r.data)).catch(()=>{});
  }, [slug, user]);

  if (notFound) return (
    <div className="max-w-lg mx-auto px-6 py-24 text-center" data-testid="cert-not-found">
      <div className="font-display text-6xl text-gold-700-gradient">404</div>
      <h1 className="font-display text-2xl text-ink-900 mt-3">Certification introuvable</h1>
      <p className="text-muted2 mt-2 text-sm">Cette certification n'existe pas ou n'est plus disponible.</p>
      <Link to="/categories" className="inline-block mt-6 px-5 py-2.5 rounded-full bg-gold-500 text-ink-900 font-medium hover:bg-gold-500-400">Voir les catégories</Link>
    </div>
  );
  if (!cert) return <div className="max-w-3xl mx-auto p-10 text-muted2 text-center font-mono text-sm">Chargement…</div>;
  const hasPurchased = access?.has_purchased;
  const completedIds = new Set(access?.completed_lesson_ids || []);

  return (
    <div className="max-w-6xl mx-auto px-6 py-12" data-testid="certification-detail-page">
      <div className="grid lg:grid-cols-[1fr,360px] gap-8">
        <div>
          <Link to={`/categories/${cert.category?.slug}`} className="text-xs uppercase tracking-widest text-gold-700 font-mono mb-4 inline-block hover:text-gold-700-700-600">
            ← Retour à {cert.category?.name}
          </Link>
          <h1 className="font-display text-4xl md:text-5xl text-ink-900 mt-2">{cert.title}</h1>
          <p className="text-muted2 mt-4 text-lg leading-relaxed">{cert.description}</p>

          {cert.category?.disclaimer && (
            <div className={`rounded-xl border p-4 mt-6 flex gap-3 ${
              cert.category.disclaimer_type === "danger" ? "bg-danger/10 border-danger/40 text-danger"
              : "bg-warning/10 border-warning/40 text-warning"
            }`} data-testid="cert-disclaimer">
              <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <p className="text-sm leading-relaxed">{cert.category.disclaimer}</p>
            </div>
          )}

          <h2 className="font-display text-2xl text-ink-900 mt-10 mb-4 flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-gold-700" /> Programme — {cert.lessons.length} leçons
          </h2>
          <div className="space-y-3">
            {cert.lessons.map((l, i) => {
              const completed = completedIds.has(l.id);
              const locked = !hasPurchased && l.display_order > 1;
              return (
                <div key={l.id} className="glass rounded-xl px-5 py-4 flex items-center gap-4" data-testid={`lesson-row-${i}`}>
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center font-mono text-sm ${
                    completed ? "bg-success/20 text-success" : "bg-gold-500/10 text-gold-700"
                  }`}>
                    {completed ? <CheckCircle2 className="w-5 h-5" /> : String(l.display_order).padStart(2, "0")}
                  </div>
                  <div className="flex-1">
                    <div className="text-ink-900 text-sm">{l.title}</div>
                    <div className="text-xs text-muted2 mt-0.5 flex items-center gap-2">
                      <Clock className="w-3 h-3" /> {l.estimated_reading_minutes} min · {l.video_key_concept || "Lecture & vidéo"}
                    </div>
                  </div>
                  {locked ? <Lock className="w-4 h-4 text-muted2" /> :
                   <span className="text-xs text-gold-700">Accès {l.display_order === 1 ? "libre" : "inclus"}</span>}
                </div>
              );
            })}
          </div>

          <h2 className="font-display text-2xl text-ink-900 mt-10 mb-4 flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-gold-700" /> Évaluation
          </h2>
          <div className="glass rounded-xl p-5 text-sm text-muted2 leading-relaxed">
            <p>
              Cette certification comprend <strong className="text-ink-900">{cert.quizzes.length} quiz</strong> de validation
              ({cert.quizzes[0]?.duration_minutes || 30} min chacun, score requis : <strong className="text-gold-700">70%</strong>).
              Vous disposez de 3 tentatives. Au terme de la formation, vous recevez un certificat PDF unique
              avec un code vérifiable par QR code.
            </p>
          </div>
        </div>

        {/* SIDEBAR */}
        <aside className="lg:sticky lg:top-24 self-start">
          <div className="glass rounded-2xl p-6 shadow-card">
            <div className="text-xs uppercase tracking-widest text-gold-700 font-mono">Prix</div>
            <div className="font-display text-5xl text-gold-700-gradient mt-1">{cert.price_euros}€</div>
            <div className="text-xs text-muted2 mt-1">paiement unique · accès à vie</div>

            <div className="gold-divider my-5" />

            <ul className="space-y-2 text-sm">
              <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-gold-700 flex-shrink-0 mt-0.5" /> {cert.lessons.length} leçons complètes</li>
              <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-gold-700 flex-shrink-0 mt-0.5" /> {cert.quizzes.length} quiz de validation</li>
              <li className="flex gap-2"><Award className="w-4 h-4 text-gold-700 flex-shrink-0 mt-0.5" /> Certificat PDF + QR code</li>
              <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-gold-700 flex-shrink-0 mt-0.5" /> Durée estimée : {cert.estimated_duration_hours}h</li>
              <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-gold-700 flex-shrink-0 mt-0.5" /> Remboursable 48h si zéro leçon ouverte</li>
            </ul>

            <div className="mt-6 space-y-2">
              {!user && (
                <button onClick={() => nav("/login?redirect=" + encodeURIComponent(`/payment/${cert.slug}`))} className="w-full px-5 py-3 rounded-full bg-gold-500 text-ink-900 font-medium hover:bg-gold-500-400 shadow-gold transition-all inline-flex items-center justify-center gap-2" data-testid="cta-login-to-buy">
                  Se connecter pour acheter <ArrowRight className="w-4 h-4" />
                </button>
              )}
              {user && !hasPurchased && (
                <Link to={`/payment/${cert.slug}`} className="block w-full px-5 py-3 rounded-full bg-gold-500 text-ink-900 font-medium hover:bg-gold-500-400 shadow-gold transition-all text-center" data-testid="cta-buy-certification">
                  Acheter cette certification
                </Link>
              )}
              {user && hasPurchased && (
                <Link to={`/learn/${cert.slug}`} className="block w-full px-5 py-3 rounded-full bg-gold-500 text-ink-900 font-medium hover:bg-gold-500-400 shadow-gold transition-all text-center" data-testid="cta-continue-learning">
                  Continuer la formation
                </Link>
              )}
              {user && cert.lessons[0] && !hasPurchased && (
                <Link to={`/learn/${cert.slug}`} className="block w-full px-5 py-3 rounded-full border border-gold-300 hover:bg-gold-500/10 transition-all text-center" data-testid="cta-preview-first-lesson">
                  Aperçu de la première leçon
                </Link>
              )}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
